import { useQuery } from '@apollo/client';
import { Delete, Image, Movie, PictureAsPdf } from '@mui/icons-material';
import { Box, Button, type DialogProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { Pptx } from '~/components/icons';
import { FolderContextProvider } from '~/contexts';
import type { ContentKind } from '~/generated/graphql';
import { ContentGrid, DialogRoot, MediaPreview } from './components';
import { MediaSelectDialogDocument } from './index.generated';

type MediaSelectKind = Extract<ContentKind, 'IMAGE' | 'PDF' | 'POWERPOINT' | 'VIDEO'> | 'MEDIA';

interface MediaSelectDialogProps extends Omit<DialogProps, 'onChange'> {
  close: () => void;
  kind: MediaSelectKind;
  networkId: number;
  onChange: (id: number) => void;
  value: number | undefined;
}

const MediaSelectDialog = ({
  close,
  kind,
  networkId,
  onChange,
  value,
  ...props
}: MediaSelectDialogProps) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(value);

  // This extra query sucks but should be temporarry
  const { data } = useQuery(MediaSelectDialogDocument, { variables: { networkId } });

  useEffect(() => setSelectedId(value), [value]);

  const onExited = () => {
    setSelectedId(value);
  };

  if (data?.network == null) return null;

  return (
    <FolderContextProvider
      folderTree={data.network.contentFolderTree}
      folders={data.network.contentFolders}
    >
      <Dialog
        PaperComponent={DialogRoot}
        TransitionProps={{ onExited }}
        fullWidth
        maxWidth="lg"
        onClose={close}
        {...props}
      >
        <DialogTitle onClose={close}>
          {t(`components.media_select.dialog.title.${kind.toLowerCase()}`)}
        </DialogTitle>

        <DialogContent sx={{ overflow: 'hidden' }}>
          <DialogContentTitle>
            {t(`components.media_select.dialog.description.${kind.toLowerCase()}`)}
          </DialogContentTitle>
          <ContentGrid
            kind={kind === 'MEDIA' ? ['IMAGE', 'PDF', 'POWERPOINT', 'VIDEO'] : [kind]}
            storageContext={`media-select-${kind.toLowerCase()}`}
            onClick={(id: string) => setSelectedId(Number(id.split('-').at(1)))}
            rememberLastFolder
            selectedIds={selectedId ? [`MediaItem-${selectedId}`] : []}
          />
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={close}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={selectedId === value}
            onClick={() => {
              if (selectedId === undefined) return;
              close();
              onChange(selectedId);
            }}
          >
            {t('common.actions.select')}
          </Button>
        </DialogActions>
      </Dialog>
    </FolderContextProvider>
  );
};

export interface MediaSelectProps {
  disabled?: boolean;
  kind: MediaSelectKind;
  mediaItemId: number | undefined;
  networkId: number;
  onChange: (mediaItemId: number | null) => void;
}

export const MediaSelect = ({
  disabled,
  kind,
  mediaItemId,
  networkId,
  onChange,
}: MediaSelectProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(mediaItemId);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => setValue(mediaItemId), [mediaItemId]);

  const previousValue = useRef(value);
  useEffect(() => {
    if (value === previousValue.current) return;
    previousValue.current = value;
    onChange(value ?? null);
  }, [onChange, value]);

  return (
    <Box
      sx={{
        alignItems: 'flex-end',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {value != null && <MediaPreview mediaItemIds={[value]} />}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {value != null && (
          <Button
            color="error"
            variant="contained"
            startIcon={<Delete />}
            onClick={() => setValue(undefined)}
            disabled={disabled}
            size="small"
          >
            {t('common.actions.remove')} {t(`components.media_select.kind.${kind.toLowerCase()}`)}
          </Button>
        )}

        <Button
          color="primary"
          variant="contained"
          startIcon={
            kind === 'IMAGE' ? (
              <Image />
            ) : kind === 'PDF' ? (
              <PictureAsPdf />
            ) : kind === 'POWERPOINT' ? (
              <Pptx />
            ) : (
              <Movie />
            )
          }
          onClick={() => setDialogOpen(true)}
          disabled={disabled}
          size="small"
        >
          {t('common.actions.select')} {t(`components.media_select.kind.${kind.toLowerCase()}`)}
        </Button>
      </Box>

      <MediaSelectDialog
        close={() => setDialogOpen(false)}
        kind={kind}
        networkId={networkId}
        onChange={setValue}
        open={dialogOpen}
        value={value}
      />
    </Box>
  );
};
